/**
 * ContractTemplateEditor - Editor rich text para modelos de contrato
 * 
 * Integra TipTap com sistema de variáveis para criação de templates
 * Suporte a preview com dados de exemplo
 * Inclui indicadores visuais de quebra de página A4
 * Usa CSS compartilhado (contractStyles.ts) para paridade com Preview e PDF
 * 
 * @release v3.0.0 - 2026-02-16 - Migrado para 96 DPI com escala visual
 * @release v2.0.0 - 2026-02-16 - Paridade visual com Preview/PDF via CSS compartilhado
 * @release v1.1.0 - 2026-02-16 - Adicionado paginação visual no editor
 * @release v1.0.0 - 2026-02-16
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
// Extensões de tabela para renderizar HTML com <table>, <tr>, <td>, <th>
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
  Link as LinkIcon,
  Minus,
  AlignLeft,
  Code,
  HelpCircle,
  BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ContractVariableButton } from './ContractVariableButton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  A4_WIDTH_PX,
  A4_HEIGHT_PX,
  CONTRACT_CONTENT_CSS,
  PREVIEW_SCALE_FACTOR,
  getUsableHeight,
  getPagePadding,
  getTopPaddingPX,
} from './contractStyles';

// Props do editor
interface ContractTemplateEditorProps {
  /** Conteúdo HTML do template */
  content: string;
  /** Callback ao alterar conteúdo */
  onChange: (html: string) => void;
  /** Modo somente leitura */
  readOnly?: boolean;
  /** Placeholder quando vazio */
  placeholder?: string;
  /** URL da imagem de fundo para cálculo de padding correto */
  backgroundPreviewUrl?: string | null;
  /** Se deve exibir numeração de páginas nos indicadores */
  showPageNumbers?: boolean;
  /** Posição da paginação: 'top-right' ou 'bottom-right' */
  pageNumberPosition?: string;
}

/**
 * Botão da toolbar com tooltip
 */
function ToolbarButton({
  icon: Icon,
  label,
  isActive,
  onClick,
  disabled,
}: {
  icon: typeof Bold;
  label: string;
  isActive?: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', isActive && 'bg-muted')}
          onClick={onClick}
          disabled={disabled}
          type="button"
        >
          <Icon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p className="text-xs">{label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Componente de indicador de quebra de página sobreposto ao editor
 * Renderiza linhas tracejadas nas posições de quebra com rótulo "Página X"
 * NOTA: As posições são em pixels reais (96 DPI), a escala é aplicada no container pai
 */
function PageBreakOverlay({
  editorContentHeight,
  usableHeight,
  hasBackground,
  showPageNumbers,
  pageNumberPosition,
}: {
  editorContentHeight: number;
  usableHeight: number;
  /** Se o template tem imagem de fundo */
  hasBackground: boolean;
  /** Se exibe numeração de páginas */
  showPageNumbers?: boolean;
  /** Posição da paginação */
  pageNumberPosition?: string;
}) {
  // Padding superior do conteúdo (offset inicial antes da primeira quebra)
  const topPadding = getTopPaddingPX(hasBackground, showPageNumbers, pageNumberPosition);
  // Altura da faixa de gap = soma das margens verticais (topo + rodapé)
  const gapHeight = A4_HEIGHT_PX - usableHeight;

  // Calcula posições das quebras de página considerando o topPadding
  const numBreaks = editorContentHeight > usableHeight
    ? Math.ceil(editorContentHeight / usableHeight) - 1
    : 0;
  const pageBreaks: number[] = [];
  for (let i = 1; i <= numBreaks; i++) {
    // Inclui topPadding para alinhar com o início real do conteúdo
    pageBreaks.push(topPadding + i * usableHeight + (i - 1) * gapHeight);
  }

  if (pageBreaks.length === 0) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 10 }}
      aria-hidden="true"
    >
      {pageBreaks.map((breakY, idx) => (
        <div
          key={idx}
          className="absolute left-0 right-0 flex items-center justify-center"
          style={{
            top: `${breakY}px`,
            height: '0px',
          }}
        >
          {/* Linha tracejada indicando quebra de página */}
          <div className="absolute inset-x-0 top-0 border-t-2 border-dashed border-destructive/40" />
          {/* Rótulo de página */}
          <span className="relative -translate-y-1/2 bg-background text-muted-foreground text-[10px] font-medium px-3 py-0.5 rounded-full border border-border/50">
            ─── Página {idx + 2} ───
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * CSS adicional para o ProseMirror no contexto do editor.
 * Extende o CONTRACT_CONTENT_CSS com regras específicas do TipTap
 * (placeholder, outline, etc.) sem alterar tipografia/margens.
 */
const EDITOR_PROSEMIRROR_CSS = `
  .contract-editor-wrapper .ProseMirror {
    outline: none;
    min-height: 280px;
  }
  .contract-editor-wrapper .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
    color: #9ca3af;
  }
  /* Estilos para tabelas dentro do editor */
  .contract-editor-wrapper .ProseMirror table {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
  }
  .contract-editor-wrapper .ProseMirror th,
  .contract-editor-wrapper .ProseMirror td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
    min-width: 50px;
  }
  .contract-editor-wrapper .ProseMirror th {
    font-weight: bold;
    background: #f5f5f5;
  }
`;

export function ContractTemplateEditor({
  content,
  onChange,
  readOnly = false,
  placeholder = 'Comece a escrever o modelo de contrato...\n\nUse o botão "Variáveis" para inserir campos dinâmicos como {{nome_cliente}}, {{valor_negocio}}, etc.',
  backgroundPreviewUrl,
  showPageNumbers,
  pageNumberPosition,
}: ContractTemplateEditorProps) {
  // Flag para evitar loop infinito entre onUpdate e useEffect de sincronização
  const isInternalUpdate = useRef(false);
  // Ref para o container do editor (medição de altura)
  const editorContainerRef = useRef<HTMLDivElement>(null);
  // Altura atual do conteúdo do editor
  const [editorContentHeight, setEditorContentHeight] = useState(0);
  // Controla se o modo HTML está ativo
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  // Controla abertura do modal de ajuda de sintaxe de variáveis
  const [helpOpen, setHelpOpen] = useState(false);
  // Armazena o HTML bruto enquanto o usuário edita no textarea
  const [rawHtml, setRawHtml] = useState('');

  // Calcula altura útil por página baseado na presença de background (96 DPI)
  const hasBackground = !!backgroundPreviewUrl;
  const usableHeight = getUsableHeight(hasBackground, showPageNumbers, pageNumberPosition);
  const pagePadding = getPagePadding(hasBackground, showPageNumbers, pageNumberPosition);

  // Total de páginas para exibição no footer
  const totalPages = Math.max(1, Math.floor(editorContentHeight / usableHeight) + 1);

  /**
   * Mede a altura do conteúdo do ProseMirror
   */
  const measureContentHeight = useCallback(() => {
    const editorElement = editorContainerRef.current?.querySelector('.ProseMirror');
    if (editorElement) {
      setEditorContentHeight(editorElement.scrollHeight);
    }
  }, []);

  // Inicializa editor TipTap
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-primary underline' },
      }),
      Image,
      Placeholder.configure({ placeholder }),
      // Extensões de tabela para suportar HTML com grids e colunas
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content,
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      isInternalUpdate.current = true;
      onChange(ed.getHTML());
      // Re-mede altura após cada alteração de conteúdo
      requestAnimationFrame(measureContentHeight);
    },
  });

  // Sincroniza conteúdo externo (prop) com o editor quando muda por fora
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const currentContent = editor.getHTML();
    if (content !== currentContent && content !== '<p></p>') {
      editor.commands.setContent(content, { emitUpdate: false });
      requestAnimationFrame(measureContentHeight);
    }
  }, [content, editor, measureContentHeight]);

  // ResizeObserver para medir altura do ProseMirror em tempo real
  useEffect(() => {
    const container = editorContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      measureContentHeight();
    });

    // Observa o container e o ProseMirror quando disponível
    observer.observe(container);
    const proseMirror = container.querySelector('.ProseMirror');
    if (proseMirror) {
      observer.observe(proseMirror);
    }

    // Medição inicial
    measureContentHeight();

    return () => observer.disconnect();
  }, [editor, measureContentHeight]);

  // Handler para inserir variável na posição do cursor
  const handleInsertVariable = useCallback(
    (variable: string) => {
      if (!editor) return;
      editor.chain().focus().insertContent(variable).run();
    },
    [editor]
  );

  // Handler para adicionar link
  const handleAddLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('URL do link:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }, [editor]);

  // Handler para alternar entre modo visual e modo HTML
  const toggleHtmlMode = useCallback(() => {
    if (!editor) return;
    if (isHtmlMode) {
      // HTML -> Visual: aplica HTML no editor TipTap
      editor.commands.setContent(rawHtml, { emitUpdate: true });
      setIsHtmlMode(false);
    } else {
      // Visual -> HTML: captura HTML do editor
      setRawHtml(editor.getHTML());
      setIsHtmlMode(true);
    }
  }, [isHtmlMode, rawHtml, editor]);

  if (!editor) return null;

  return (
    <div className="border rounded-lg overflow-hidden flex flex-col" style={{ background: '#e8e8e8' }}>
      {/* Injeção do CSS compartilhado + CSS específico do editor */}
      <style dangerouslySetInnerHTML={{ __html: CONTRACT_CONTENT_CSS + EDITOR_PROSEMIRROR_CSS }} />

      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center flex-wrap gap-0.5 p-1.5 border-b bg-muted/80">
          {/* Formatação de texto */}
          <ToolbarButton
            icon={Bold}
            label="Negrito"
            isActive={editor.isActive('bold')}
            onClick={() => (editor.chain().focus() as any).toggleBold().run()}
            disabled={isHtmlMode}
          />
          <ToolbarButton
            icon={Italic}
            label="Itálico"
            isActive={editor.isActive('italic')}
            onClick={() => (editor.chain().focus() as any).toggleItalic().run()}
            disabled={isHtmlMode}
          />

          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Headings */}
          <ToolbarButton
            icon={Heading1}
            label="Título 1"
            isActive={editor.isActive('heading', { level: 1 })}
            onClick={() => (editor.chain().focus() as any).toggleHeading({ level: 1 }).run()}
            disabled={isHtmlMode}
          />
          <ToolbarButton
            icon={Heading2}
            label="Título 2"
            isActive={editor.isActive('heading', { level: 2 })}
            onClick={() => (editor.chain().focus() as any).toggleHeading({ level: 2 }).run()}
            disabled={isHtmlMode}
          />
          <ToolbarButton
            icon={Heading3}
            label="Título 3"
            isActive={editor.isActive('heading', { level: 3 })}
            onClick={() => (editor.chain().focus() as any).toggleHeading({ level: 3 }).run()}
            disabled={isHtmlMode}
          />

          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Listas */}
          <ToolbarButton
            icon={List}
            label="Lista"
            isActive={editor.isActive('bulletList')}
            onClick={() => (editor.chain().focus() as any).toggleBulletList().run()}
            disabled={isHtmlMode}
          />
          <ToolbarButton
            icon={ListOrdered}
            label="Lista Numerada"
            isActive={editor.isActive('orderedList')}
            onClick={() => (editor.chain().focus() as any).toggleOrderedList().run()}
            disabled={isHtmlMode}
          />

          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Utilitários */}
          <ToolbarButton
            icon={LinkIcon}
            label="Link"
            isActive={editor.isActive('link')}
            onClick={handleAddLink}
            disabled={isHtmlMode}
          />
          <ToolbarButton
            icon={Minus}
            label="Linha Horizontal"
            onClick={() => (editor.chain().focus() as any).setHorizontalRule().run()}
            disabled={isHtmlMode}
          />
          <ToolbarButton
            icon={AlignLeft}
            label="Parágrafo"
            onClick={() => (editor.chain().focus() as any).setParagraph().run()}
            disabled={isHtmlMode}
          />

          {/* Botão HTML - alterna entre modo visual e modo código */}
          <ToolbarButton
            icon={Code}
            label={isHtmlMode ? 'Voltar ao editor visual' : 'Editar HTML'}
            isActive={isHtmlMode}
            onClick={toggleHtmlMode}
          />

          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Undo/Redo */}
          <ToolbarButton
            icon={Undo}
            label="Desfazer"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={isHtmlMode || !editor.can().undo()}
          />
          <ToolbarButton
            icon={Redo}
            label="Refazer"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={isHtmlMode || !editor.can().redo()}
          />

          {/* Espaçador */}
          <div className="flex-1" />

          {/* Botão de ajuda de sintaxe de variáveis */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setHelpOpen(true)}
                type="button"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="text-xs">Guia de sintaxe de variáveis</p>
            </TooltipContent>
          </Tooltip>

          {/* Indicador de páginas na toolbar */}
          <span className="text-xs text-muted-foreground mr-2 ml-2">
            {totalPages} {totalPages === 1 ? 'página' : 'páginas'}
          </span>

          {/* Botão de variáveis */}
          <ContractVariableButton onInsert={handleInsertVariable} />
        </div>
      )}

      {/* ===== Modal de ajuda: Guia de Sintaxe de Variáveis ===== */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden">
          {/* Cabeçalho com gradiente */}
          <DialogHeader className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-b">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 p-2.5 rounded-xl bg-primary/10 text-primary">
                <BookOpen className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground">
                  Guia de Sintaxe de Variáveis
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Aprenda a usar modificadores nos campos do modelo de contrato
                </p>
              </div>
            </div>
          </DialogHeader>

          {/* Conteúdo com scroll */}
          <ScrollArea className="max-h-[calc(85vh-180px)]">
            <div className="px-6 py-5 space-y-5">

              {/* Seção 1: Campo Simples */}
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/60 border-b border-border/60">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Campo Simples</span>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <code className="inline-block px-2.5 py-1 rounded-md bg-primary/8 border border-primary/20 text-primary font-mono text-sm font-medium">
                    {`{{nome_campo}}`}
                  </code>
                  <p className="text-sm text-muted-foreground">
                    Campo de texto livre. O usuário preenche livremente no wizard de geração de contrato.
                  </p>
                  <div className="mt-2 p-2.5 rounded-md bg-muted/40 border border-border/40">
                    <span className="text-xs font-medium text-muted-foreground">Exemplo no contrato:</span>
                    <p className="text-sm text-foreground mt-0.5 font-mono">
                      {`"Contratante: {{nome_cliente}}, CPF: {{cpf_cliente}}"`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Seção 2: Campo Obrigatório */}
              <div className="rounded-lg border border-destructive/30 overflow-hidden">
                <div className="px-4 py-2.5 bg-destructive/8 border-b border-destructive/20">
                  <span className="text-xs font-semibold uppercase tracking-wider text-destructive/80">Campo Obrigatório  ★</span>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <code className="inline-block px-2.5 py-1 rounded-md bg-destructive/8 border border-destructive/25 text-destructive font-mono text-sm font-medium">
                    {`{{*nome_campo}}`}
                  </code>
                  <p className="text-sm text-muted-foreground">
                    O asterisco <span className="font-semibold text-destructive">*</span> antes do nome marca o campo como obrigatório. O wizard bloqueia o avanço até que o campo seja preenchido.
                  </p>
                  <div className="mt-2 p-2.5 rounded-md bg-muted/40 border border-border/40">
                    <span className="text-xs font-medium text-muted-foreground">Exemplo no contrato:</span>
                    <p className="text-sm text-foreground mt-0.5 font-mono">
                      {`"CPF do Contratante: {{*cpf_cliente}}"`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Seção 3: Valor Padrão */}
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/60 border-b border-border/60">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Valor Padrão  ||</span>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <code className="inline-block px-2.5 py-1 rounded-md bg-primary/8 border border-primary/20 text-primary font-mono text-sm font-medium">
                    {`{{combustivel||gasolina}}`}
                  </code>
                  <p className="text-sm text-muted-foreground">
                    Use <span className="font-mono font-semibold text-foreground">||</span> para definir um valor padrão. O campo aparece pré-preenchido no wizard. Se o usuário não alterar, o valor padrão é usado.
                  </p>
                  <div className="mt-2 p-2.5 rounded-md bg-muted/40 border border-border/40">
                    <span className="text-xs font-medium text-muted-foreground">Exemplo no contrato:</span>
                    <p className="text-sm text-foreground mt-0.5 font-mono">
                      {`"O veículo utiliza {{combustivel||gasolina}} como combustível"`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Seção 4: Seleção Única */}
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/60 border-b border-border/60">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Seleção Única  [A, B, C]</span>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <code className="inline-block px-2.5 py-1 rounded-md bg-primary/8 border border-primary/20 text-primary font-mono text-sm font-medium">
                    {`{{tipo_veiculo[Carro, Moto, Caminhão]}}`}
                  </code>
                  <p className="text-sm text-muted-foreground">
                    Colchetes com opções separadas por <span className="font-mono font-semibold text-foreground">, (vírgula)</span> criam um dropdown de seleção única no wizard.
                  </p>
                  <div className="mt-2 p-2.5 rounded-md bg-muted/40 border border-border/40">
                    <span className="text-xs font-medium text-muted-foreground">Exemplo no contrato:</span>
                    <p className="text-sm text-foreground mt-0.5 font-mono">
                      {`"Tipo do veículo: {{tipo_veiculo[Carro, Moto, Caminhão]}}"`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Seção 5: Múltipla Escolha */}
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/60 border-b border-border/60">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Múltipla Escolha  [A; B; C]</span>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <code className="inline-block px-2.5 py-1 rounded-md bg-primary/8 border border-primary/20 text-primary font-mono text-sm font-medium">
                    {`{{itens[Alarme; Rastreador; GPS]}}`}
                  </code>
                  <p className="text-sm text-muted-foreground">
                    Colchetes com opções separadas por <span className="font-mono font-semibold text-foreground">; (ponto-e-vírgula)</span> criam checkboxes de múltipla escolha no wizard.
                  </p>
                  <div className="mt-2 p-2.5 rounded-md bg-muted/40 border border-border/40">
                    <span className="text-xs font-medium text-muted-foreground">Exemplo no contrato:</span>
                    <p className="text-sm text-foreground mt-0.5 font-mono">
                      {`"Opcionais incluídos: {{itens[Alarme; Rastreador; GPS]}}"`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Seção 6: Combinação */}
              <div className="rounded-lg border border-border/60 overflow-hidden">
                <div className="px-4 py-2.5 bg-secondary/50 border-b border-border/60">
                  <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">Combinação de Modificadores  ⚡</span>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <code className="inline-block px-2.5 py-1 rounded-md bg-secondary/40 border border-border/50 text-secondary-foreground font-mono text-sm font-medium">
                    {`{{*combustivel||gasolina[Gasolina, Etanol, GNV]}}`}
                  </code>
                  <p className="text-sm text-muted-foreground">
                    Os modificadores podem ser combinados na mesma variável. O exemplo acima é <span className="font-semibold text-foreground">obrigatório</span> + <span className="font-semibold text-foreground">valor padrão "gasolina"</span> + <span className="font-semibold text-foreground">lista de opções</span>.
                  </p>
                  <div className="p-3 rounded-md bg-muted/60 border border-border/50 space-y-1">
                    <p className="text-xs font-medium text-foreground">Ordem dos modificadores:</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {`{{`}<span className="text-destructive font-bold">*</span>chave<span className="text-primary font-bold">||padrão</span><span className="text-foreground font-bold">[Op1, Op2]</span>{`}}`}
                    </p>
                  </div>
                  <div className="mt-2 p-2.5 rounded-md bg-muted/40 border border-border/40">
                    <span className="text-xs font-medium text-muted-foreground">Exemplo no contrato:</span>
                    <p className="text-sm text-foreground mt-0.5 font-mono">
                      {`"Combustível: {{*combustivel||gasolina[Gasolina, Etanol, GNV]}}"`}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </ScrollArea>

          {/* Rodapé */}
          <div className="px-6 py-4 border-t bg-muted/30">
            <Button
              onClick={() => setHelpOpen(false)}
              className="w-full"
              size="lg"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Área de edição - modo HTML (textarea) ou modo visual (TipTap) */}
      {isHtmlMode ? (
        <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
          <textarea
            className="w-full min-h-[400px] p-4 font-mono text-sm bg-background border-0 outline-none resize-none"
            value={rawHtml}
            onChange={(e) => {
              setRawHtml(e.target.value);
              onChange(e.target.value);
            }}
            spellCheck={false}
            placeholder="Cole ou edite seu HTML aqui..."
          />
        </div>
      ) : (
        <div
          ref={editorContainerRef}
          className="relative overflow-y-auto"
          style={{ maxHeight: '60vh' }}
        >
          {/* Container de escala - transforma 794px → ~595px visual */}
          <div
            style={{
              width: `${A4_WIDTH_PX * PREVIEW_SCALE_FACTOR}px`,
              margin: '16px auto',
            }}
          >
            {/* Área do editor simulando papel branco - largura A4 real a 96 DPI */}
            <div
              className="bg-background shadow-md contract-editor-wrapper"
              style={{
                width: `${A4_WIDTH_PX}px`,
                minHeight: (() => {
                  const topPad = getTopPaddingPX(hasBackground, showPageNumbers, pageNumberPosition);
                  return Math.max(300, topPad + editorContentHeight);
                })(),
                position: 'relative',
                transform: `scale(${PREVIEW_SCALE_FACTOR})`,
                transformOrigin: 'top left',
              }}
            >
              {/* Conteúdo editável do TipTap */}
              <div className="contract-content">
                <EditorContent
                  editor={editor}
                  className="min-h-[300px]"
                  style={{
                    padding: pagePadding,
                  }}
                />
              </div>

              {/* Overlay de indicadores de quebra de página */}
              <PageBreakOverlay
                editorContentHeight={editorContentHeight}
                usableHeight={usableHeight}
                hasBackground={hasBackground}
                showPageNumbers={showPageNumbers}
                pageNumberPosition={pageNumberPosition}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
